import React from "react"

import { useTranslation } from "react-i18next"

import { StakeValidationIssue } from "services/bonus/type"

import { formatStake, formatTier, isSameTier, minorToTier, stakeIssueMessage, tierToMinor } from "../../helpers/spin-stake.helpers"

import { controlLineClassName, inputClassName, labelClassName, selectClassName } from "./bonus-form-ui"
import { GameBetValuesCtx, SyncOutcome } from "./useGameBetValues"

/**
 * SOFT-736 — the per-currency spin stake, chosen from the game's own bet ladder.
 *
 * A stake that is not a real tier of the game does not fail: the launch serves an ordinary game
 * and quietly withholds the spins the player was granted. The picker is what makes that
 * impossible to configure — and it is also what keeps the units straight, since the ladder speaks
 * major units (`20` = 20 CLP) while the platform stores minor ones (`2000`), for every currency.
 */

const CUSTOM_VALUE = "__custom__"

interface SpinStakeCellProps {
  coin: string
  /** The stored stake, in `coin`'s minor units. */
  value?: number
  onChange: (minor: number | undefined) => void
  betValues: GameBetValuesCtx
  /** No game selected yet — nothing to pick from, so typing is the only option. */
  hasGame: boolean
  /** Save-time issues that belong to this row (wrong tier / missing ladder). */
  issues?: StakeValidationIssue[]
  label?: string
  /**
   * This cell edits the template's USD FALLBACK rather than a currency's own stake. Two things
   * follow, and both are the opposite of a row's:
   *  - it is NOT validated against the ladder (the launch converts it and snaps it to the nearest
   *    real tier), so an off-ladder value here is legitimate and must not be flagged;
   *  - it cannot be CLEARED through this API: `mergeAdminIntoTemplate` treats an absent/null
   *    money field as "not provided" and keeps the stored value, so offering "no stake" would
   *    report a change that never happened.
   */
  isFallback?: boolean
}

export const SpinStakeCell: React.FC<SpinStakeCellProps> = ({
  coin,
  value,
  onChange,
  betValues,
  hasGame,
  issues = [],
  label,
  isFallback = false
}) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const tiers = hasGame ? betValues.tiersFor(coin) : null
  const rowIssue = issues[0]

  const hint = (text: string, tone: "muted" | "warn" = "muted") => (
    <span className={`mt-1 text-xs ${tone === "warn" ? "text-amber-300" : "text-gray-400"}`}>{text}</span>
  )

  const body = () => {
    // No game yet: there is no ladder to choose from, so the operator types — in MAJOR units,
    // which is what the ladder (and the rest of the world) calls this amount.
    if (!hasGame) {
      return (
        <>
          <input
            type="number"
            step="any"
            min="0"
            value={value === undefined || value === null ? "" : minorToTier(value)}
            onChange={(e) => {
              const major = parseFloat(e.target.value)
              onChange(Number.isFinite(major) ? tierToMinor(major) : undefined)
            }}
            className={inputClassName}
            aria-label={t("bonus2.config.spinStakeAria", { coin }) as string}
          />
          {hint(t("bonus2.config.stakeTypeAmount", { coin: coin.toUpperCase() }) as string)}
        </>
      )
    }

    if (betValues.state === "loading") {
      return (
        <select className={selectClassName} disabled aria-label={t("bonus2.config.spinStakeAria", { coin }) as string}>
          <option>{t("bonus2.config.loadingBetValues")}</option>
        </select>
      )
    }

    // The game's provider has no ladders at all — no sync and no retry can produce tiers, so the
    // cell says so instead of offering a button that cannot work.
    if (betValues.state === "not_supported") {
      return <>{hint(t("bonus2.config.stakeNotSupported") as string, "warn")}</>
    }

    // 503 / 409 — the ladders could not be READ, or a sync is already running. Not "no tiers":
    // keep whatever is selected and offer a retry, or the outage silently becomes an empty picker
    // and the operator picks nothing.
    if (betValues.state === "unavailable" || betValues.state === "in_progress") {
      return (
        <>
          <div className={`${controlLineClassName} gap-2`}>
            <span className="flex-1 truncate rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
              {formatStake(value, coin)}
            </span>
            <button type="button" onClick={betValues.reload} className="btn btn-xs btn-ghost">
              {t("bonus2.config.retry")}
            </button>
          </div>
          {hint(
            betValues.state === "in_progress"
              ? (t("bonus2.config.stakeSyncRunning") as string)
              : (t("bonus2.config.stakeUnavailable") as string),
            "warn"
          )}
        </>
      )
    }

    if (betValues.state === "not_found") {
      return <>{hint(t("bonus2.config.stakeGameNotFound") as string, "warn")}</>
    }

    // The game answered, but has no ladder for THIS currency: syncing is the fix, not re-picking.
    if (!tiers || tiers.length === 0) {
      return (
        <>
          <div className={`${controlLineClassName} gap-2`}>
            <span className="flex-1 truncate rounded border border-[rgba(132,0,168,0.35)] px-3 py-2 text-sm text-gray-400">
              {formatStake(value, coin)}
            </span>
            <button type="button" onClick={() => betValues.sync([coin])} disabled={betValues.syncing} className="btn btn-xs btn-primary">
              {betValues.syncing ? t("bonus2.config.syncing") : t("bonus2.config.sync")}
            </button>
          </div>
          {hint(
            tiers === null && betValues.ladders.length > 1
              ? (t("bonus2.config.stakeSomeGamesMissing", { coin: coin.toUpperCase() }) as string)
              : (t("bonus2.config.stakeGameMissing", { coin: coin.toUpperCase() }) as string),
            "warn"
          )}
        </>
      )
    }

    const hasValue = value !== undefined && value !== null
    const isKnownTier = hasValue && tiers.some((t) => isSameTier(t, minorToTier(value as number)))
    // The stored fallback cannot be removed through this API (see `isFallback`), so the "no stake"
    // option is disabled rather than silently ineffective.
    const clearDisabled = isFallback && hasValue
    return (
      <>
        <select
          className={selectClassName}
          value={value === undefined || value === null ? "" : isKnownTier ? String(value) : CUSTOM_VALUE}
          onChange={(e) => {
            const next = e.target.value
            onChange(next === "" || next === CUSTOM_VALUE ? undefined : Number(next))
          }}
          aria-label={t("bonus2.config.spinStakeAria", { coin }) as string}
        >
          <option value="" disabled={clearDisabled}>
            {isFallback ? t("bonus2.config.noFallbackStake") : t("bonus2.config.noStakeUsesFallback")}
          </option>
          {/* A stored value that is NOT a tier stays visible and stays selected — hiding it would
              turn "this bonus is misconfigured" into "this bonus has no stake". */}
          {!isKnownTier && hasValue && (
            <option value={CUSTOM_VALUE}>
              {formatStake(value, coin)}
              {isFallback ? t("bonus2.config.snappedAtLaunch") : t("bonus2.config.notABetValue")}
            </option>
          )}
          {tiers.map((tier) => (
            <option key={tier} value={tierToMinor(tier)}>
              {formatTier(tier, coin)}
            </option>
          ))}
        </select>
        {!isKnownTier &&
          hasValue &&
          // The fallback is NEVER checked against a ladder — it is converted into the player's
          // currency and snapped to the nearest real tier at launch — so warning about it here
          // would call the one legitimate off-ladder value an error.
          !isFallback &&
          hint(t("bonus2.config.stakeNotATier") as string, "warn")}
        {clearDisabled && hint(t("bonus2.config.fallbackNotClearable") as string)}
      </>
    )
  }

  return (
    <div className="form-control">
      <label className="label py-1">
        <span className={labelClassName}>{label ?? (t("bonus2.config.spinStake") as string)}</span>
      </label>
      {body()}
      {rowIssue && <span className="mt-1 text-xs text-rose-400">{stakeIssueMessage(rowIssue, t)}</span>}
    </div>
  )
}

/** The outcome of one sync. `fetched` and `stillMissing` are independent facts, so both lines can
 *  show at once; the neutral line covers "the provider returned nothing new", which is a real
 *  answer and must not be reported as silence. */
const SyncOutcomeLines: React.FC<{ outcome: SyncOutcome }> = ({ outcome }) => {
  const fetched = outcome.fetched ?? []
  return (
    <>
      {fetched.length > 0 && <p className="mt-2 text-xs text-emerald-400">Sync finished — bet values updated: {fetched.join(", ")}.</p>}
      {outcome.stillMissing.length > 0 && (
        <p className="mt-2 text-xs text-amber-300">
          The provider returned no bet values for {outcome.stillMissing.join(", ")} — this game may simply not be priced for
          {outcome.stillMissing.length > 1 ? " these currencies" : " that currency"}.
        </p>
      )}
      {fetched.length === 0 && outcome.stillMissing.length === 0 && (
        <p className="mt-2 text-xs text-gray-400">Sync finished — the provider returned nothing new; the stored bet values are unchanged.</p>
      )}
    </>
  )
}

interface BetValuesSyncCardProps {
  betValues: GameBetValuesCtx
  hasGame: boolean
}

/**
 * FE-3 — refresh the selected games' ladders, and show which currencies still have none.
 *
 * One game per call by design; the chips sync a single currency each. Never loop this over a
 * catalogue: the sweep it replaced issued ~78.5k failing provider calls in one run.
 */
export const BetValuesSyncCard: React.FC<BetValuesSyncCardProps> = ({ betValues, hasGame }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  // A provider that has no ladders and a game that is not in the catalog are answered by
  // choosing another game; the read states are answered by Retry, which lives in the cell.
  const hasSomethingToSync = betValues.state === "empty" || betValues.missingFiat.length > 0

  if (!hasGame) return null

  return (
    <div className="rounded-lg border border-[rgba(132,0,168,0.25)] p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm dark:text-gray-300">
          Bet values
          {betValues.state === "ready" && <span className="ml-2 text-xs text-emerald-400">loaded</span>}
          {betValues.state === "empty" && <span className="ml-2 text-xs text-amber-300">{t("bonus2.config.notStoredForEvery")}</span>}
          {betValues.state === "unavailable" && <span className="ml-2 text-xs text-amber-300">{t("bonus2.config.unavailableRetry")}</span>}
          {betValues.state === "in_progress" && <span className="ml-2 text-xs text-amber-300">{t("bonus2.config.aSyncIsAlready")}</span>}
          {betValues.state === "not_supported" && <span className="ml-2 text-xs text-rose-400">{t("bonus2.config.thisProviderHasNo")}</span>}
          {betValues.state === "not_found" && <span className="ml-2 text-xs text-rose-400">game not in this tenant&apos;s catalog</span>}
        </span>
        {/* Offered only when there is something to fetch. Bet values are read from the provider
            here and nowhere else, so a stored ladder is simply shown — re-fetching one the game
            already has spends a provider call to arrive at what is on screen. */}
        {hasSomethingToSync && (
          <button
            type="button"
            onClick={() => betValues.sync()}
            disabled={betValues.syncing || betValues.state === "in_progress"}
            className="btn btn-xs btn-primary"
          >
            {betValues.syncing ? t("bonus2.config.syncing") : t("bonus2.config.syncBetValues")}
          </button>
        )}
      </div>

      {betValues.state === "unavailable" && (
        <p className="mt-2 text-xs text-amber-300">
          {t("bonus2.config.theTierListCould")}
        </p>
      )}

      {betValues.state === "in_progress" && (
        <p className="mt-2 text-xs text-amber-300">
          {t("bonus2.config.aSyncForThis")}
        </p>
      )}

      {betValues.state === "not_supported" && (
        <p className="mt-2 text-xs text-rose-400">
          This game&apos;s provider has no bet values at all, so a free-spins stake can never resolve on it. Pick a different game.
        </p>
      )}

      {betValues.state === "empty" && (
        <p className="mt-2 text-xs text-amber-300">
          {t("bonus2.config.noBetValuesAre")}
        </p>
      )}

      {/* SOFT-736 — what the last sync actually did. A sync the provider answered with nothing is
          a SUCCESS that changes nothing, and saying nothing about it is indistinguishable from a
          button that does not work. The two lines are independent (a sync can fetch one currency
          and miss another), and the third makes sure some line always appears. */}
      {betValues.lastSync && <SyncOutcomeLines outcome={betValues.lastSync} />}

      {betValues.missingFiat.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-gray-400">{t("bonus2.config.missing")}</span>
          {betValues.missingFiat.map((coin) => (
            <button
              key={coin}
              type="button"
              onClick={() => betValues.sync([coin])}
              disabled={betValues.syncing}
              className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-200 hover:bg-amber-500/20"
              title={t("bonus2.config.syncCoinTitle", { coin }) as string}
            >
              {coin}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
